import React from 'react';
import { Box, TextField, FormControlLabel, Switch, Chip, Autocomplete, Checkbox } from '@mui/material';
import type { HandballSettings, HandballTieBreaker } from '@/types';
import type { SportSettingsFormProps } from './types';
import { SettingsSection } from './SettingsSection';
import { SettingsField, SettingsFieldsGrid } from './SettingsFieldsGrid';

const TIEBREAKER_OPTIONS: { value: HandballTieBreaker; label: string }[] = [
  { value: 'goal_difference', label: 'Разница мячей' },
  { value: 'goals_scored',    label: 'Забитые мячи' },
  { value: 'head_to_head',    label: 'Личные встречи' },
];

export const HandballSettingsForm: React.FC<SportSettingsFormProps> = ({ value, onChange, disabled }) => {
  const s = value as HandballSettings;
  const upd = (patch: Partial<HandballSettings>) => onChange({ ...s, ...patch });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

      <SettingsSection title="Состав и структура">
        <SettingsFieldsGrid>
          <SettingsField>
            <TextField label="Игроков на площадке" type="number" size="small" fullWidth
              value={s.playersOnField ?? 7}
              onChange={(e) => upd({ playersOnField: parseInt(e.target.value) || 7 })}
              slotProps={{ htmlInput: { min: 1, max: 7 } }}
              disabled={disabled} />
          </SettingsField>
          <SettingsField>
            <TextField label="Таймов" type="number" size="small" fullWidth
              value={s.periods ?? 2}
              onChange={(e) => upd({ periods: parseInt(e.target.value) || 2 })}
              slotProps={{ htmlInput: { min: 1 } }}
              disabled={disabled} />
          </SettingsField>
          <SettingsField>
            <TextField label="Длина тайма, мин" type="number" size="small" fullWidth
              value={s.periodDurationMin ?? 30}
              onChange={(e) => upd({ periodDurationMin: parseInt(e.target.value) || 30 })}
              slotProps={{ htmlInput: { min: 1 } }}
              disabled={disabled} />
          </SettingsField>
        </SettingsFieldsGrid>
      </SettingsSection>

      <SettingsSection title="Очки">
        <SettingsFieldsGrid>
          <SettingsField>
            <TextField label="За победу" type="number" size="small" fullWidth
              value={s.pointsWin ?? 2}
              onChange={(e) => upd({ pointsWin: parseInt(e.target.value) })}
              slotProps={{ htmlInput: { min: 0 } }} disabled={disabled} />
          </SettingsField>
          <SettingsField>
            <TextField label="За ничью" type="number" size="small" fullWidth
              value={s.pointsDraw ?? 1}
              onChange={(e) => upd({ pointsDraw: parseInt(e.target.value) })}
              slotProps={{ htmlInput: { min: 0 } }} disabled={disabled} />
          </SettingsField>
          <SettingsField>
            <TextField label="За поражение" type="number" size="small" fullWidth
              value={s.pointsLoss ?? 0}
              onChange={(e) => upd({ pointsLoss: parseInt(e.target.value) })}
              slotProps={{ htmlInput: { min: 0 } }} disabled={disabled} />
          </SettingsField>
        </SettingsFieldsGrid>
      </SettingsSection>

      <SettingsSection title="Овертайм">
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, flexWrap: 'wrap', gap: 1 }}>
          <FormControlLabel
            control={<Switch checked={s.overtime ?? true}
              onChange={(e) => upd({ overtime: e.target.checked })} disabled={disabled} />}
            label="Овертайм"
          />
          <FormControlLabel
            control={<Switch checked={s.shootout ?? true}
              onChange={(e) => upd({ shootout: e.target.checked })} disabled={disabled} />}
            label="Серия бросков"
          />
        </Box>
      </SettingsSection>

      <SettingsSection title="Тай-брейки">
        <Autocomplete
          multiple
          disableCloseOnSelect
          options={TIEBREAKER_OPTIONS.map((o) => o.value)}
          value={s.tieBreakers ?? []}
          onChange={(_, v) => upd({ tieBreakers: v as HandballTieBreaker[] })}
          getOptionLabel={(o) => TIEBREAKER_OPTIONS.find((x) => x.value === o)?.label ?? o}
          disabled={disabled}
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Checkbox checked={selected} size="small" sx={{ mr: 1 }} />
              {TIEBREAKER_OPTIONS.find((x) => x.value === option)?.label ?? option}
            </li>
          )}
          renderTags={(val, getTagProps) =>
            val.map((opt, idx) => (
              <Chip label={`${idx + 1}. ${TIEBREAKER_OPTIONS.find((x) => x.value === opt)?.label ?? opt}`}
                size="small" {...getTagProps({ index: idx })} key={opt} />
            ))
          }
          renderInput={(params) => <TextField {...params} size="small" fullWidth placeholder="Добавить критерий..." />}
        />
      </SettingsSection>

    </Box>
  );
};
